import { useState, useRef, useEffect } from 'react';
import styled, { css } from 'styled-components';
// compnents
import CheckBox from 'cds/forms/CheckBox';
import FillButton from 'cds/buttons/Fill';
import Icon from 'cds/icons';
// slices
import { postSendAuth, postCheckSmsAuth } from 'slices/user.thunk';
// hooks
import { useDispatch } from 'hooks/common/useStore';
import { isExistWindow } from 'lib/utils/helper';
import useRedirectUrl from 'hooks/domain/review/useRedirectUrl';
// styles
import * as typography from 'cds/typography';
import { palette, mediaQuery } from 'cds/styles';

/**
 * 오직 숫자값 리턴하기
 * @param {number} value
 * @returns number
 */
function setOnlyNumbers(value) {
  return value.replace(/[^\d*]/g, '');
}

/**
 * 시작, 끝 여백 지우기
 * @param {number} value
 * @returns value
 */
function setTrimValue(value) {
  return value.trim();
}

function removeSpecialCharacter(value) {
  return value.replace(/[~`₩!@#$%^&*()_|+\-=?;:'",.<>{}[\]\\/]/g, '');
}
/**
 * 휴대폰 회원가입 페이지
 * @param {object} props
 * @param {Func} props.onSignin         휴대폰 번호 로그인 페이지 이동 이벤트
 * @param {Func} props.onSigninSocial   소셜 로그인 페이지 이동 이벤트
 */
const SignupPhone = ({ onSignin, onSigninSocial }) => {
  const dispatch = useDispatch();

  const [nickname, setNickname] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [password, setPassword] = useState('');
  const [verifyPassword, setVerifyPassword] = useState('');
  const [certificationNumber, setCertificationNumber] = useState('');
  const [isTos, setIsTos] = useState(true);

  const [isWrongPhoneNumber, setIsWrongPhoneNumber] = useState(false);
  const [isRequestCertification, setIsRequestCertification] = useState(false);
  const [isVisibleValidMessageOfCertification, setIsVisibleValidMessageOfCertification] =
    useState(false);

  const [isVisiblePassword, setIsVisiblePassword] = useState(false);
  const [isVisibleVerifyPassword, setIsVisibleVerifyPassword] = useState(false);

  const [isVisibleValidMessageOfNickname, setIsVisibleValidMessageOfNickname] = useState(false);
  const [isVisibleValidMessageOfPassword, setIsVisibleValidMessageOfPassword] = useState(false);
  const [isVisibleValidMessageOfVerifyPassword, setIsVisibleValidMessageOfVerifyPassword] =
    useState(false);

  const [isSendAuth, setIsSendAuth] = useState(false);
  const [isReSendAuth, setIsReSendAuth] = useState(false);
  const [authMessage, setAuthMessage] = useState('ready');
  const [isPeriod, setIsPeriod] = useState(false);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);

  const [queryParams, setQueryParams] = useState('');

  const certificationTimer = useRef(null);
  const availableTime = useRef(null);
  const formRef = useRef(null);

  const { redirectUrl } = useRedirectUrl();

  const isPhoneNumberValid = phoneNumber.length >= 11;
  const isCertificationNumberValid = certificationNumber.length >= 4;

  const onKeyUp = e => {
    if (isSubmitDisabled) {
      return;
    }

    if (e.keyCode !== 13) {
      return;
    }

    formRef.current.submit();
  };

  /** 닉네임 인풋 이벤트 */
  const onInputNickname = e => {
    const { value } = e.target;
    setNickname(removeSpecialCharacter(setTrimValue(value)));
    setIsVisibleValidMessageOfNickname(false);
  };

  const onBlurNickname = e => {
    const { value } = e.target;
    if (setTrimValue(value).length >= 2) {
      return;
    }

    setIsVisibleValidMessageOfNickname(true);
  };

  /** 휴대폰 번호 인풋 이벤트 */
  const onInputPhoneNumber = e => {
    const { value } = e.target;
    setPhoneNumber(setOnlyNumbers(value));
    setIsWrongPhoneNumber(false);
    setIsRequestCertification(false);
  };

  const onBlurPhoneNumber = e => {
    const { value } = e.target;
    if (value.length >= 11) {
      return;
    }

    setIsWrongPhoneNumber(true);
  };

  /** 인증번호 인풋 이벤트 */
  const onInputCertificationNumber = e => {
    const { value } = e.target;
    setCertificationNumber(setOnlyNumbers(value));
    setIsVisibleValidMessageOfCertification(false);
  };

  /** 비밀번호 토글 */
  const onTogglePassword = () => {
    setIsVisiblePassword(v => !v);
  };

  /** 확인용 비밀번호 토글 */
  const onToggleVerifyPassword = () => {
    setIsVisibleVerifyPassword(v => !v);
  };

  /** 인증 번호 발송 요청 이벤트 */
  const onRequestAuth = () => {
    setIsPeriod(false);
    dispatch(postSendAuth({ phone: phoneNumber }));
  };

  /** 인증 번호 발송 요청 */
  const onAuth = () => {
    setIsRequestCertification(false);
    setIsSendAuth(true);
    onRequestAuth();
  };

  /** 인증 번호 재발송 요청 */
  const onReSendAuth = () => {
    setIsRequestCertification(false);
    setIsReSendAuth(false);
    onRequestAuth();
  };

  /** 인증 번호 확인 */
  const onCheckAuth = async () => {
    if (!isPhoneNumberValid && !isCertificationNumberValid) {
      return;
    }

    const res = await dispatch(
      postCheckSmsAuth({
        hpno: phoneNumber,
        authno: certificationNumber,
      }),
    );

    if (res.payload.message !== 'success') {
      setCertificationNumber('');
      setIsVisibleValidMessageOfCertification(true);
    }

    setAuthMessage(res.payload.message);
  };

  /** 비밀번호 인풋 이벤트 */
  const onInputPassword = e => {
    const { value } = e.target;
    setPassword(value);
    setIsVisibleValidMessageOfPassword(false);
  };

  const onBlurPassword = e => {
    const { value } = e.target;
    if (value.length >= 6) {
      return;
    }

    setIsVisibleValidMessageOfPassword(true);
  };

  /** 확인 비밀번호 인풋 이벤트 */
  const onInputVerifyPassword = e => {
    const { value } = e.target;
    setVerifyPassword(value);
    setIsVisibleValidMessageOfVerifyPassword(false);
  };

  const onBlurVerifyPassword = e => {
    const { value } = e.target;
    if (password === value) {
      return;
    }

    setIsVisibleValidMessageOfVerifyPassword(true);
  };

  /** 인증 유효 시간 작업 */
  useEffect(() => {
    if (!isSendAuth) {
      return;
    }

    const initTimer = () => {
      window.clearTimeout(certificationTimer.current);
      certificationTimer.current = null;

      setIsReSendAuth(true);
      setIsPeriod(true);
    };

    if (authMessage === 'success') {
      return initTimer();
    }

    if (certificationTimer.current) {
      return;
    }

    const target = availableTime.current;

    const onTimer = time => {
      if (time === 0 && authMessage !== 'success') {
        setCertificationNumber('');
        setIsVisibleValidMessageOfCertification(false);
      }

      if (time <= 0 || isPeriod) {
        initTimer();
      } else {
        const minute = Math.floor(time / 60);
        const second = time % 60 < 10 ? `0${time % 60}` : time % 60;

        target.textContent = `${minute}:${second}`;
        certificationTimer.current = window.setTimeout(() => onTimer(time - 1), 1000);
      }
    };

    onTimer(60 * 3);

    return () => {
      if (authMessage !== 'success') {
        return;
      }

      window.clearTimeout(certificationTimer.current);
    };
  }, [isSendAuth, availableTime, authMessage, isPeriod]);

  /** 리턴 url 설정 */
  useEffect(() => {
    if (!isExistWindow()) {
      return;
    }

    const params = window.location.search;
    if (params) {
      setQueryParams(params);
      return;
    }

    if (redirectUrl.indexOf('/user/sigin') > -1) {
      return;
    }

    setQueryParams(`?url=${redirectUrl}`);
  }, [redirectUrl]);

  /** 회원가입 완료 버튼 활성화 */
  useEffect(() => {
    const isValid =
      nickname.length >= 2 &&
      authMessage === 'success' &&
      password &&
      password === verifyPassword &&
      isTos;

    if (isValid) {
      return setIsSubmitDisabled(false);
    }

    return setIsSubmitDisabled(true);
  }, [nickname, authMessage, password, verifyPassword, isTos]);

  return (
    <>
      <Section>
        <Inner>
          <Title>회원가입</Title>
          <Form
            ref={formRef}
            method="POST"
            action={`${process.env.NEXT_PUBLIC_DEFAULT_HOST}/modoo/registerUser${queryParams}`}
            acceptCharset="UTF-8"
            target="_top"
          >
            <div>
              <Field>
                <label htmlFor="username">
                  닉네임<i>*</i>
                </label>
                <div>
                  <Input
                    id="username"
                    name="username"
                    type="text"
                    minLength="2"
                    maxLength="8"
                    autoComplete="username"
                    placeholder="닉네임을 입력해주세요 (8자 제한)"
                    value={nickname}
                    onInput={onInputNickname}
                    onKeyUp={onKeyUp}
                    onBlur={onBlurNickname}
                    required
                  />
                  {isVisibleValidMessageOfNickname && <Valid>닉네임은 최소 2자 입력해주세요</Valid>}
                </div>
              </Field>

              <Authenticate active={isSendAuth}>
                <label htmlFor="phone">
                  휴대폰 번호<i>*</i>
                </label>
                <div>
                  <PhoneInputField
                    id="phone"
                    name="phone"
                    type="tel"
                    inputMode="numeric"
                    maxLength="11"
                    autoComplete="tel"
                    placeholder="숫자만 입력해 주세요"
                    readOnly={(isSendAuth && !isReSendAuth) || authMessage === 'success'}
                    required
                    value={phoneNumber}
                    onInput={onInputPhoneNumber}
                    onKeyUp={onKeyUp}
                    onBlur={onBlurPhoneNumber}
                  />
                  <AddOns isSendAuth={isSendAuth}>
                    {!isSendAuth && (
                      <AuthorizingButton
                        type="button"
                        onClick={onAuth}
                        disabled={!isPhoneNumberValid}
                      >
                        인증요청
                      </AuthorizingButton>
                    )}

                    {isReSendAuth && isPeriod && authMessage !== 'success' && (
                      <ReAuthorizingButton
                        type="button"
                        onClick={onReSendAuth}
                        disabled={!isPhoneNumberValid}
                      >
                        인증번호 재발송
                      </ReAuthorizingButton>
                    )}

                    {isSendAuth && !isReSendAuth && !isPeriod && (
                      <TimeMessage ref={availableTime} />
                    )}
                  </AddOns>

                  {isWrongPhoneNumber && <Valid>휴대폰 번호를 입력해주세요</Valid>}
                </div>

                {isSendAuth && (
                  <div>
                    <AuthInputFeild
                      name="authno"
                      type="tel"
                      inputMode="numeric"
                      maxLength="4"
                      autoComplete="one-time-code"
                      placeholder="인증번호 4자리를 입력해 주세요"
                      readOnly={!isSendAuth || isReSendAuth || authMessage === 'success'}
                      required
                      value={certificationNumber}
                      onInput={onInputCertificationNumber}
                    />
                    <AddOns>
                      {isSendAuth && !isReSendAuth && isCertificationNumberValid && (
                        <AuthoDo type="button" tabIndex="-1" onClick={onCheckAuth}>
                          인증하기
                        </AuthoDo>
                      )}

                      {authMessage === 'success' && <AuthDoneMessage>인증완료</AuthDoneMessage>}
                    </AddOns>

                    {isVisibleValidMessageOfCertification && authMessage !== 'success' && (
                      <Valid>{authMessage}</Valid>
                    )}
                  </div>
                )}
                {isRequestCertification && authMessage !== 'success' && (
                  <Valid>휴대폰 번호를 인증해주세요</Valid>
                )}
              </Authenticate>

              {authMessage === 'success' && (
                <>
                  <Field>
                    <label htmlFor="password">
                      비밀번호<i>*</i>
                    </label>
                    <div>
                      <Input
                        id="password"
                        name="password"
                        type={isVisiblePassword ? 'text' : 'password'}
                        maxLength="30"
                        autoComplete="new-password"
                        placeholder="비밀번호는 6자리 이상 입력해주세요"
                        value={password}
                        onInput={onInputPassword}
                        onKeyUp={onKeyUp}
                        onBlur={onBlurPassword}
                        required
                      />

                      <AddOns>
                        <TogglePasswordButton
                          type="button"
                          tabIndex="-1"
                          onClick={onTogglePassword}
                        >
                          <Icon
                            name={
                              isVisiblePassword ? 'ic_visible_password' : 'ic_invisible_password'
                            }
                            width={24}
                            height={24}
                            fill={password.length ? palette.grey90 : palette.grey040}
                          />
                        </TogglePasswordButton>
                      </AddOns>
                      {isVisibleValidMessageOfPassword && (
                        <Valid>비밀번호는 6자리 이상 입력해주세요</Valid>
                      )}
                    </div>
                  </Field>

                  <Field>
                    <label htmlFor="password_confirmation">
                      비밀번호 확인<i>*</i>
                    </label>
                    <div>
                      <Input
                        id="password_confirmation"
                        name="password_confirmation"
                        type={isVisibleVerifyPassword ? 'text' : 'password'}
                        maxLength="30"
                        autoComplete="new-password"
                        placeholder="비밀번호를 한 번 더 입력해 주세요"
                        value={verifyPassword}
                        onInput={onInputVerifyPassword}
                        onKeyUp={onKeyUp}
                        onBlur={onBlurVerifyPassword}
                        required
                      />

                      <AddOns>
                        <TogglePasswordButton
                          type="button"
                          tabIndex="-1"
                          onClick={onToggleVerifyPassword}
                        >
                          <Icon
                            name={
                              isVisibleVerifyPassword
                                ? 'ic_visible_password'
                                : 'ic_invisible_password'
                            }
                            width={24}
                            height={24}
                            fill={verifyPassword.length ? palette.grey90 : palette.grey040}
                          />
                        </TogglePasswordButton>
                      </AddOns>

                      {isVisibleValidMessageOfVerifyPassword && (
                        <Valid>비밀번호가 일치하지 않습니다</Valid>
                      )}
                    </div>
                  </Field>
                </>
              )}
            </div>

            <Option
              isValid={
                isVisibleValidMessageOfVerifyPassword ||
                ((isRequestCertification || isVisibleValidMessageOfCertification) &&
                  authMessage !== 'success') ||
                isWrongPhoneNumber
              }
            >
              <CheckBox
                name="agree"
                label="tos"
                message="약관 및 개인정보 취급방침 동의"
                value={isTos ? '1' : '0'}
                checked={isTos}
                onChange={() => setIsTos(v => !v)}
              />
            </Option>

            <SignupButton type="submit" disabled={isSubmitDisabled ? true : false}>
              완료
            </SignupButton>
          </Form>

          <MoreLayout>
            <button type="button" onClick={onSignin}>
              로그인
            </button>
            <span>│</span>
            <button type="button" onClick={onSigninSocial}>
              다른 방법으로 시작하기
            </button>
          </MoreLayout>
        </Inner>
      </Section>
    </>
  );
};

const Section = styled.section`
  width: 100%;
  max-width: 530px;
  padding: 16px;
  margin: 0 auto;
`;

const Inner = styled.div`
  max-width: 386px;
  margin: 0 auto;
`;

const Title = styled.h2`
  margin-bottom: 50px;
  font-size: 21px;
  font-weight: 700;
  line-height: 34px;
  text-align: center;
  color: ${palette.grey75};
  ${mediaQuery.large} {
    font-size: 24px;
    line-height: 42px;
  }
`;

const TogglePasswordButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  z-index: 1;
`;

const Option = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  margin: ${({ isValid }) => (isValid ? '16px 0 52px;' : '10px 0 52px;')};

  & > label {
    color: ${palette.grey25};
  }

  span {
    background-color: ${palette.BTN.BG};
    border: 1px solid ${palette.grey0};
  }
`;

const SignupButton = styled(FillButton)`
  width: 100%;
  height: 58px;
  margin: 0 0 20px;
  background-color: ${palette.Primary.Active};
  color: ${palette.white};
  border-radius: 5px;
  font-size: 16px;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:disabled {
    background-color: ${palette.BTN.BG};
    color: ${palette.grey0};
  }

  &:active {
    filter: brightness(90%);
  }
`;

const MoreLayout = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  ${typography.body3_regular}

  & > span {
    color: ${palette.grey50};
  }

  & > button {
    padding: 0 6px;
    color: ${palette.grey050};
    ${typography.body3_regular}
  }
`;

const CertificationStyle = css`
  height: 36px;
  padding: 0 10px;
  border-radius: 4px;
  background-color: rgba(0, 0, 0, 0);
  text-align: center;
  line-height: 32px;
  font-size: 14px;
  font-weight: 400;
`;

const AuthorizingButton = styled.button`
  ${CertificationStyle}
  background-color: ${palette.red040};
  color: ${palette.white};
  font-size: 14px;
  transition: background-color 0.2s ease, color 0.2s ease;

  &:disabled {
    background-color: ${palette.BTN.BG};
    color: ${palette.grey0};
  }

  &:active {
    opacity: 0.6;
  }
`;

const TimeMessage = styled.p`
  color: ${palette.semantic.emphasis};
  ${typography.body4_regular}
`;

const AuthDoneMessage = styled.p`
  padding: 0 6px;
  color: ${palette.semantic.emphasis};
  ${typography.body4_regular}
`;

const Field = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: 16px;
  font-size: 16px;

  /* &:last-of-type {
    margin-bottom: 10px;
  } */

  & label {
    display: block;
    padding-left: 14px;
    margin-bottom: 6px;
    color: ${palette.grey50};
  }

  & i {
    color: ${palette.red040};
  }

  & > div {
    position: relative;
  }
`;

const Authenticate = styled.div`
  margin-bottom: 16px;

  & label {
    display: block;
    padding-left: 14px;
    margin-bottom: 6px;
    color: ${palette.grey50};

    & i {
      color: ${palette.red040};
    }
  }

  & > div {
    position: relative;
  }

  ${({ active }) =>
    active
      ? css`
          & div:first-of-type {
            input {
              border-bottom-left-radius: 0;
              border-bottom-right-radius: 0;
            }
          }
        `
      : css``}

  & div ~ div input {
    margin-top: -1px;
    border-top-left-radius: 0;
    border-top-right-radius: 0;
  }

  & button,
  & a,
  & p,
  & span {
    z-index: 2;
  }
`;

const InputStyles = css`
  width: 100%;
  height: 52px;
  padding: 0 14px;
  border: none;
  outline: none;
  border-radius: 8px;
  font-weight: 400;
  font-size: 16px;
  line-height: 12px;
  background-color: ${palette.system.BNB};
  color: ${palette.grey75};

  &:-webkit-autofill,
  &:-webkit-autofill:hover,
  &:-webkit-autofill:focus,
  &:-webkit-autofill:active {
    box-shadow: 0 0 0 30px white inset !important;
  }

  &:focus {
    position: relative;
    border: 1px solid ${palette.grey060};
    z-index: 1;
  }

  &:placeholder-shown {
    text-overflow: ellipsis;
  }
  s &::placeholder {
    color: ${palette.grey0};
    font-size: inherit;
  }

  &:focus::placeholder {
    color: ${palette.grey0};
  }
`;

const Input = styled.input`
  ${InputStyles}
  border: 1px solid ${palette.system.grey};
`;

const PhoneInputField = styled.input`
  ${InputStyles}
  padding: 0 112px 0 14px;
  border: 1px solid ${palette.system.grey};
`;

const AuthInputFeild = styled.input`
  ${InputStyles}
  padding: 0 76px 0 14px;
  border: 1px solid ${palette.system.grey};
`;

const AddOns = styled.div`
  display: flex;
  position: absolute;
  align-items: center;
  justify-content: flex-end;
  height: 52px;
  padding: ${({ isSendAuth }) => (isSendAuth ? '0 14px' : '0 8px')};
  top: 0;
  right: 0;
`;

const ReAuthorizingButton = styled.button`
  ${typography.body4_regular}
  color: ${palette.red060};
  z-index: 2;
`;

const AuthoDo = styled.button`
  ${typography.body4_regular}
  color: ${palette.red060};
  z-index: 2;
`;

const Valid = styled.div`
  padding-left: 14px;
  margin-top: 5px;
  ${typography.body4_regular}
  color: ${palette.red060};
`;

const Form = styled.form`
  & > div:first-of-type > div:last-of-type {
    margin-bottom: 10px;
  }
`;

export default SignupPhone;
